/** The grass and foliage paint layers of a container (see GrassPaint and FoliagePaint in format.ts). */
import { FOLIAGE_SHIFT, LINE_B, LINE_BITS, LINE_L, LINE_R, LINE_S, LINE_T, foliageFields, type FoliageLayer, type FoliagePaint, type GrassLines, type GrassPaint, type TerrainShape, type Vec2 } from '../model/format.ts';
import { slopeSegment } from '../terrain/shapes.ts';
import { ringBounds } from './polygon.ts';

export type GrassMode = 'paint' | 'erase' | 'restore';
export type FoliageMode = 'add' | 'remove' | 'restore';

// ------------------------------------------------------------------ per-line grass

/** One outline line of a surface cell (container-local), from a to b. */
export interface SurfaceLine {
  x: number;
  y: number;
  /** LINE_* bit */
  bit: number;
  a: Vec2;
  b: Vec2;
}

const SIDE_LINES = [LINE_L, LINE_R, LINE_B, LINE_T];

/** The lines of surface cells (terrainSurface: [x, y, own, lines, halves, ...]) as segments. */
export function surfaceLines(surface: number[]): SurfaceLine[] {
  const out: SurfaceLine[] = [];
  for (let i = 0; i + 4 < surface.length; i += 5) {
    const [x, y, own, lines, halves] = [surface[i], surface[i + 1], surface[i + 2], surface[i + 3], surface[i + 4]];
    for (let s = 0; s < 4; s++) {
      if (!(lines & SIDE_LINES[s])) continue;
      const hb = (halves >> (2 * s)) & 3;
      const t0 = hb & 1 ? 0 : 0.5, t1 = hb & 2 ? 1 : 0.5;
      const pt = (t: number): Vec2 => (s === 0 ? [x, y + t] : s === 1 ? [x + 1, y + t] : s === 2 ? [x + t, y] : [x + t, y + 1]);
      out.push({ x, y, bit: SIDE_LINES[s], a: pt(t0), b: pt(t1) });
    }
    if (lines & LINE_S) {
      const seg = slopeSegment(own);
      if (seg) out.push({ x, y, bit: LINE_S, a: [x + seg[0][0], y + seg[0][1]], b: [x + seg[1][0], y + seg[1][1]] });
    }
  }
  return out;
}

function segDistance(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
  return Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t));
}

/** The line nearest to a point, within `reach` tiles. */
export function lineAt(lines: SurfaceLine[], p: Vec2, reach: number): SurfaceLine | null {
  let best: SurfaceLine | null = null, bd = reach;
  for (const l of lines) {
    if (Math.abs(l.x + 0.5 - p[0]) > reach + 1 || Math.abs(l.y + 0.5 - p[1]) > reach + 1) continue;
    const d = segDistance(p, l.a, l.b);
    if (d < bd) {
      bd = d;
      best = l;
    }
  }
  return best;
}

export const sameLine = (a: { x: number; y: number; bit: number } | null, b: { x: number; y: number; bit: number } | null) => !!a && !!b && a.x === b.x && a.y === b.y && a.bit === b.bit;

/** Lines whose middle lies in the cell rectangle [x0, x1] × [y0, y1]. */
export function linesInRect(lines: SurfaceLine[], x0: number, y0: number, x1: number, y1: number): SurfaceLine[] {
  return lines.filter((l) => {
    const mx = (l.a[0] + l.b[0]) / 2, my = (l.a[1] + l.b[1]) / 2;
    return mx >= x0 && mx <= x1 + 1 && my >= y0 && my <= y1 + 1;
  });
}

/** The lines from `from` to `to` along the outline they are on (both included), or null when they are not connected. */
export function linePath(lines: SurfaceLine[], from: { x: number; y: number; bit: number }, to: { x: number; y: number; bit: number }): SurfaceLine[] | null {
  const key = (p: Vec2) => Math.round(p[0] * 2) + ',' + Math.round(p[1] * 2);
  const byEnd = new Map<string, number[]>();
  lines.forEach((l, i) => {
    for (const k of [key(l.a), key(l.b)]) {
      let arr = byEnd.get(k);
      if (!arr) byEnd.set(k, (arr = []));
      arr.push(i);
    }
  });
  const start = lines.findIndex((l) => sameLine(l, from));
  const goal = lines.findIndex((l) => sameLine(l, to));
  if (start < 0 || goal < 0) return null;
  const prev = new Int32Array(lines.length).fill(-2);
  prev[start] = -1;
  const queue = [start];
  for (let qi = 0; qi < queue.length && prev[goal] === -2; qi++) {
    const i = queue[qi];
    for (const k of [key(lines[i].a), key(lines[i].b)])
      for (const j of byEnd.get(k)!)
        if (prev[j] === -2) {
          prev[j] = i;
          queue.push(j);
        }
  }
  if (prev[goal] === -2) return null;
  const out: SurfaceLine[] = [];
  for (let i = goal; i >= 0; i = prev[i]) out.push(lines[i]);
  return out.reverse();
}

/** New line marks with the lines painted (grass), erased (no grass) or back to following their cell. */
export function applyGrassLines(marks: GrassLines | undefined, lines: { x: number; y: number; bit: number }[], mode: GrassMode): GrassLines | undefined {
  const m = new Map<string, [number, number, number]>();
  if (marks) for (let i = 0; i + 2 < marks.length; i += 3) m.set(marks[i] + ',' + marks[i + 1], [marks[i], marks[i + 1], marks[i + 2]]);
  let changed = false;
  for (const { x, y, bit } of lines) {
    const k = x + ',' + y;
    const cur = m.get(k)?.[2] ?? 0;
    const off = bit << LINE_BITS;
    const v = mode === 'paint' ? (cur | bit) & ~off : mode === 'erase' ? (cur | off) & ~bit : cur & ~(bit | off);
    if (v === cur) continue;
    if (v) m.set(k, [x, y, v]);
    else m.delete(k);
    changed = true;
  }
  if (!changed) return marks;
  if (!m.size) return undefined;
  return [...m.values()].flat();
}

/** The state of one line in the marks: 1 grass, 0 no grass, undefined follows the cell. */
export function lineMark(v: number, bit: number): 0 | 1 | undefined {
  return v & bit ? 1 : v & (bit << LINE_BITS) ? 0 : undefined;
}

/** Foliage paint reach around the terrain: tall decorations (trees) stand on cells this far from it. */
export const FOLIAGE_REACH = 8;

/** The cells (of the given ones) close enough to a shape for a decoration to cover them. */
export function foliageCells(terrains: TerrainShape[], cells: [number, number][]): [number, number][] {
  const boxes = terrains.filter((t) => !t.hidden && t.mode === 'add' && t.rings.length).map((t) => ringBounds(t.rings[0]));
  return cells.filter(([x, y]) => boxes.some(([x0, y0, x1, y1]) => x >= x0 - FOLIAGE_REACH && x < x1 + FOLIAGE_REACH && y >= y0 - FOLIAGE_REACH && y < y1 + FOLIAGE_REACH));
}

/** New foliage paint with the given kind bits of the given decoration layers added, kept out or back to the generated result on the cells. */
export function applyFoliage(
  paint: FoliagePaint | undefined,
  cells: [number, number][],
  mode: FoliageMode,
  bits: number,
  layers: FoliageLayer[] = ['front', 'back'],
): FoliagePaint | undefined {
  const m = new Map<string, [number, number, number]>();
  if (paint) for (let i = 0; i + 2 < paint.length; i += 3) m.set(paint[i] + ',' + paint[i + 1], [paint[i], paint[i + 1], paint[i + 2]]);
  let changed = false;
  for (const [x, y] of cells) {
    const k = x + ',' + y;
    const cur = m.get(k)?.[2] ?? 0;
    let v = cur;
    for (const l of layers) {
      let [add, rem] = foliageFields(cur, l);
      if (mode === 'add') {
        add |= bits;
        rem &= ~bits;
      } else if (mode === 'remove') {
        rem |= bits;
        add &= ~bits;
      } else {
        add &= ~bits;
        rem &= ~bits;
      }
      const sh = FOLIAGE_SHIFT[l];
      v = ((v & ~(0xffff << sh)) | ((add | (rem << 8)) << sh)) >>> 0;
    }
    if (v === cur) continue;
    if (v) m.set(k, [x, y, v]);
    else m.delete(k);
    changed = true;
  }
  if (!changed) return paint;
  if (!m.size) return undefined;
  return [...m.values()].flat();
}

/** Move the paint (grass or foliage) around a shape that is moved by (dx, dy). */
export function shiftGrass<P extends GrassPaint | FoliagePaint>(paint: P | undefined, t: TerrainShape, dx: number, dy: number): P | undefined {
  if (!paint?.length || !t.rings.length) return paint;
  const [x0, y0, x1, y1] = ringBounds(t.rings[0]);
  const out = paint.slice() as P;
  for (let i = 0; i + 2 < out.length; i += 3) {
    const x = out[i], y = out[i + 1];
    if (x >= x0 - 1 && x < x1 + 1 && y >= y0 - 1 && y < y1 + 1) {
      out[i] = x + dx;
      out[i + 1] = y + dy;
    }
  }
  return out;
}

/** Read a container's first-version foliage paint (one set of bits for both layers) into decoPaint. */
export function upgradeFoliagePaint(c: { foliagePaint?: number[]; decoPaint?: FoliagePaint }): boolean {
  if (!c.foliagePaint) return false;
  const old = c.foliagePaint;
  delete c.foliagePaint;
  if (!old.length || c.decoPaint?.length) return true;
  const out: number[] = [];
  for (let i = 0; i + 2 < old.length; i += 3) {
    const v = old[i + 2] & 0xffff;
    out.push(old[i], old[i + 1], (v | (v << FOLIAGE_SHIFT.back)) >>> 0);
  }
  c.decoPaint = out;
  return true;
}
